/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Grid, Card, Typography, Skeleton, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import ElectricalServicesRounded from '@mui/icons-material/ElectricalServicesRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { dashboardApi } from '../../../api/dashboardApi';
import useMounted from '../../../hooks/useMounted';
import { getServiceLabel } from '../users/UsersListTable';

const ACCENT = '#f97316';

// Intégrations : valeurs constantes pour le moment (à brancher plus tard sur la config réelle)
const INTEGRATIONS = [
  { name: 'Email', configured: false },
  { name: 'SMS', configured: false },
  { name: 'WhatsApp', configured: false },
  { name: 'Téléphonie', configured: false }
];

// Lien d'action en en-tête ("Gérer →" / "Configurer →"). Sans `to`, rendu en placeholder non cliquable.
const ActionLink = ({ to, children }) => {
  const sx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.3,
    color: ACCENT,
    fontWeight: 700,
    fontSize: '0.8rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: to ? 'pointer' : 'default',
    opacity: to ? 1 : 0.6,
    '&:hover': { textDecoration: to ? 'underline' : 'none' }
  };
  return (
    <Typography
      component={to ? RouterLink : 'span'}
      to={to || undefined}
      sx={sx}
      title={to ? undefined : 'Bientôt disponible'}
    >
      {children}
      <ArrowForwardRounded sx={{ fontSize: '0.95rem' }} />
    </Typography>
  );
};

// Coque commune des cartes (en-tête icône + titre + action, puis contenu)
const OrgCard = ({ icon, title, note, action, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: '16px',
        backgroundColor: isDark ? 'rgba(30, 41, 59, 0.6)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        boxShadow: isDark ? '0 4px 20px -2px rgba(0,0,0,0.3)' : '0 4px 20px -2px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
        '&:hover': {
          boxShadow: isDark
            ? `0 12px 26px -6px rgba(0,0,0,0.5), 0 0 14px ${ACCENT}33`
            : `0 12px 26px -6px rgba(0,0,0,0.06), 0 0 12px ${ACCENT}26`,
          borderColor: `${ACCENT}66`
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: '11px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${ACCENT}1f`,
              color: ACCENT
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: 'text.primary' }}>
            {title}
          </Typography>
          {note && (
            <Typography
              component="span"
              sx={{ fontStyle: 'italic', fontSize: '0.8rem', color: 'text.secondary' }}
            >
              {note}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {children}
    </Card>
  );
};

const StatusBadge = ({ configured, isDark }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.4,
      borderRadius: '8px',
      fontSize: '0.72rem',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      backgroundColor: configured
        ? 'rgba(16,185,129,0.12)'
        : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(100,116,139,0.12)'),
      color: configured ? '#10b981' : 'text.secondary'
    }}
  >
    {configured ? 'Configuré' : 'Non configuré'}
  </Box>
);

const DashboardOrgRow = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mounted = useMounted();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState({});

  const getTeam = useCallback(async () => {
    try {
      const result = await dashboardApi.getTeam(localStorage.getItem('accessToken'));
      if (mounted.current && result) {
        setTeam(result);
      }
    } catch (err) {
      console.error('Erreur chargement équipe dashboard:', err);
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  }, [mounted]);

  useEffect(() => {
    getTeam();
  }, [getTeam]);

  const byService = team.byService || [];

  return (
    <Grid container spacing={2}>
      {/* Carte Équipe */}
      <Grid item xs={12} md={6}>
        <OrgCard
          icon={<ShieldRounded sx={{ fontSize: '1.3rem' }} />}
          title="Équipe"
          action={<ActionLink to="/dashboard/users">Gérer</ActionLink>}
        >
          {loading ? (
            <>
              <Skeleton variant="text" width={220} height={40} animation="wave" />
              <Skeleton variant="text" width="60%" height={24} animation="wave" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="55%" height={24} animation="wave" />
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: 'text.primary', lineHeight: 1 }}>
                  {team.total ?? 0}
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  collaborateurs · {team.active ?? 0} actifs
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {byService.map((s) => (
                  <Box
                    key={s.id_service}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                      {getServiceLabel(s.id_service)}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 30,
                        textAlign: 'center',
                        px: 1,
                        py: 0.25,
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: 'text.secondary',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)'
                      }}
                    >
                      {s.count}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </OrgCard>
      </Grid>

      {/* Carte Intégrations */}
      <Grid item xs={12} md={6}>
        <OrgCard
          icon={<ElectricalServicesRounded sx={{ fontSize: '1.3rem' }} />}
          title="Intégrations"
          note="( À développer )"
          action={<ActionLink>Configurer</ActionLink>}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {INTEGRATIONS.map((it) => (
              <Box
                key={it.name}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Typography sx={{ color: 'text.primary', fontSize: '0.9rem' }}>{it.name}</Typography>
                <StatusBadge configured={it.configured} isDark={isDark} />
              </Box>
            ))}
          </Box>
        </OrgCard>
      </Grid>
    </Grid>
  );
};

export default DashboardOrgRow;
